import { useState } from "react";
import { regions, years, sortOptions } from "@/mocks/videos";
import type { VideoFilter } from "@/lib/videoUtils";

interface FilterBarProps {
  filter: VideoFilter;
  onChange: (next: VideoFilter) => void;
  compact?: boolean;
}

const chipClass = (active: boolean) =>
  `whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-medium transition-colors cursor-pointer ${
    active
      ? "bg-accent-500 text-background-950"
      : "bg-secondary-100/10 text-foreground-300 hover:bg-secondary-100/20 hover:text-foreground-50"
  }`;

export default function FilterBar({ filter, onChange, compact = false }: FilterBarProps) {
  const [open, setOpen] = useState(false);

  const set = (key: keyof VideoFilter, value: string) => onChange({ ...filter, [key]: value });
  const reset = () => onChange({ region: "全部", year: "全部", sort: "hot" });

  const chipGroup = (
    label: string,
    icon: string,
    options: { value: string; label: string }[],
    key: keyof VideoFilter
  ) => (
    <div className="flex flex-wrap items-center gap-2">
      <span className="flex items-center gap-1 whitespace-nowrap text-xs font-medium text-secondary-400">
        <i className={`${icon} text-sm`} />
        {label}
      </span>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => set(key, opt.value)}
            className={chipClass(filter[key] === opt.value)}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );

  const regionOptions = regions.map((r) => ({ value: r, label: r }));
  const yearOptions = years.map((y) => ({ value: y, label: y }));
  const sortList = sortOptions.map((s) => ({ value: s.id, label: s.label }));

  const controls = (
    <>
      {chipGroup("地区", "ri-earth-line", regionOptions, "region")}
      {chipGroup("年份", "ri-calendar-line", yearOptions, "year")}
      {chipGroup("排序", "ri-sort-desc", sortList, "sort")}
    </>
  );

  if (compact) {
    return (
      <div className="md:hidden">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex w-full items-center justify-between rounded-md border border-background-700 bg-background-900 px-4 py-3 text-sm font-medium text-foreground-100 cursor-pointer"
        >
          <span className="flex items-center gap-2">
            <i className="ri-filter-3-line text-lg text-accent-400" />
            筛选条件
          </span>
          <i className={open ? "ri-arrow-up-s-line text-xl" : "ri-arrow-down-s-line text-xl"} />
        </button>
        {open && (
          <div className="mt-3 flex flex-col gap-4 rounded-md border border-background-800 bg-background-900 p-4">
            {controls}
            <button
              type="button"
              onClick={reset}
              className="mt-1 flex items-center justify-center gap-1 whitespace-nowrap rounded-md border border-background-700 px-3 py-2 text-xs font-medium text-foreground-200 transition-colors hover:border-accent-400 hover:text-accent-400 cursor-pointer"
            >
              <i className="ri-refresh-line text-sm" />
              重置筛选
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="hidden flex-col gap-4 rounded-lg border border-background-800 bg-background-900 p-5 md:flex">
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-2 font-heading text-sm font-semibold text-foreground-100">
          <i className="ri-filter-3-line text-lg text-accent-400" />
          筛选与排序
        </span>
        <button
          type="button"
          onClick={reset}
          className="flex items-center gap-1 whitespace-nowrap text-xs font-medium text-secondary-400 transition-colors hover:text-accent-400 cursor-pointer"
        >
          <i className="ri-refresh-line text-sm" />
          重置筛选
        </button>
      </div>
      {controls}
    </div>
  );
}